import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Dumbbell,
  Copy,
  LayoutGrid,
  Download,
  Loader2,
  Pencil,
  Upload,
  Table2,
  Sparkles,
  Trash2,
  X
} from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { EmptyState } from '../components/ui/empty-state'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { useToast } from '../components/ui/toast'
import { useAuth } from '../hooks/useAuth'
import {
  createExercise,
  deleteAllExercises,
  deleteExercise,
  Exercise,
  ExerciseCategory,
  ExercisePayload,
  EXERCISE_CATEGORIES,
  EXERCISE_LOCATIONS,
  EXERCISE_SIDEDNESS_OPTIONS,
  getExercises,
  ExerciseLocation,
  ExerciseSidedness,
  PackUsage,
  PACK_USAGE_OPTIONS,
  updateExercise
} from '../lib/api/exercises'
import { buildHeaderIndex, downloadCsv, makeGetValue, normalizeText, parseBooleanValue, parseCsvText, parseNumberOrNull } from '../utils/csv'

const initialForm: ExercisePayload = {
  code: '',
  name: '',
  category: 'Elevation',
  isTrueElevation: false,
  isArtificialElevation: false,
  contributesToDistance: false,
  packUsage: 'NoPack',
  isKneeFriendly: true,
  sidedness: 'Sided',
  videoKey: null,
  secondaryVideoKey: null,
  thumbnailKey: null,
  exerciseTrackerInstructionKey: null,
  shortDescription: null,
  safetyNotes: null,
  caloriesPerMinute: null,
  isActive: true,
  exerciseLocation: 'Either'
}

const formatDate = (value?: string | null) => {
  if (!value) {
    return 'Recently added'
  }
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(new Date(value))
  } catch (error) {
    console.warn('Failed to format date', error)
    return value
  }
}

const categoryColors: Record<ExerciseCategory, string> = {
  Elevation: 'bg-green-100 text-green-800',
  Strength: 'bg-blue-100 text-blue-800',
  Balance: 'bg-purple-100 text-purple-800',
  Lunge: 'bg-indigo-100 text-indigo-800',
  HIIT: 'bg-orange-100 text-orange-800',
  Cardio: 'bg-red-100 text-red-800',
  Flexibility: 'bg-pink-100 text-pink-800',
  Recovery: 'bg-teal-100 text-teal-800'
}

const exerciseLocationColors: Record<ExerciseLocation, string> = {
  Inside: 'bg-cyan-100 text-cyan-700',
  Outside: 'bg-orange-100 text-orange-700',
  Either: 'bg-emerald-100 text-emerald-700'
}

type ViewMode = 'cards' | 'table'
type SortDirection = 'asc' | 'desc'
type ExerciseSortKey =
  | 'name'
  | 'code'
  | 'category'
  | 'packUsage'
  | 'isActive'
  | 'exerciseLocation'
  | 'createdAt'

type SortConfig = {
  key: ExerciseSortKey
  direction: SortDirection
}

type ExerciseImportAction = 'create' | 'update'

type ParsedCsvExerciseImportRow = {
  action: ExerciseImportAction
  payload: ExercisePayload
  targetId?: string
}

const compareExercises = (a: string, b: string, direction: SortDirection): number =>
  direction === 'asc' ? a.localeCompare(b) : b.localeCompare(a)

const compareBooleans = (a: boolean, b: boolean, direction: SortDirection): number => {
  const normalized = Number(a) - Number(b)
  return direction === 'asc' ? normalized : -normalized
}

const compareDates = (direction: SortDirection, a?: string | null, b?: string | null): number => {
  const aTime = a ? new Date(a).getTime() : 0
  const bTime = b ? new Date(b).getTime() : 0

  if (Number.isNaN(aTime) || Number.isNaN(bTime)) {
    return 0
  }

  const diff = aTime - bTime
  return direction === 'asc' ? diff : -diff
}

const packUsageRank: Record<PackUsage, number> = {
  NoPack: 0,
  Optional: 1,
  Required: 2
}

const exerciseLocationRank: Record<ExerciseLocation, number> = {
  Either: 0,
  Inside: 1,
  Outside: 2
}

const EXERCISE_CSV_COLUMNS = [
  'Code',
  'Name',
  'Category',
  'IsTrueElevation',
  'IsArtificialElevation',
  'ContributesToDistance',
  'PackUsage',
  'IsKneeFriendly',
  'ExerciseLocation',
  'Sidedness',
  'VideoKey',
  'SecondaryVideoKey',
  'ThumbnailKey',
  'ExerciseTrackerInstructionKey',
  'ShortDescription',
  'SafetyNotes',
  'CaloriesPerMinute',
  'IsActive'
] as const

const normalizeExerciseCode = (value: string) => value.trim().toUpperCase().replace(/\s+/g, '_')

type CsvColumnKey =
  | 'Code'
  | 'Name'
  | 'Category'
  | 'IsTrueElevation'
  | 'IsArtificialElevation'
  | 'ContributesToDistance'
  | 'PackUsage'
  | 'IsKneeFriendly'
  | 'ExerciseLocation'
  | 'Sidedness'
  | 'VideoKey'
  | 'SecondaryVideoKey'
  | 'ThumbnailKey'
  | 'ExerciseTrackerInstructionKey'
  | 'ShortDescription'
  | 'SafetyNotes'
  | 'CaloriesPerMinute'
  | 'IsActive'

const EXERCISE_HEADER_ALIASES: Record<CsvColumnKey, string[]> = {
  Code: ['code'],
  Name: ['name'],
  Category: ['category'],
  IsTrueElevation: ['istrueelevation', 'trueelevation', 'te'],
  IsArtificialElevation: ['isartificialelevation', 'artificialelevation', 'ae', 'artificial'],
  ContributesToDistance: ['contributestodistance', 'tocontribute', 'contributedistance', 'distance'],
  PackUsage: ['packusage', 'pack'],
  IsKneeFriendly: ['iskneefriendly', 'kneefriendly', 'kneefriendly'],
  ExerciseLocation: ['exerciselocation', 'location'],
  Sidedness: ['sidedness', 'sidehandling', 'sides', 'side'],
  VideoKey: ['videokey', 'video'],
  SecondaryVideoKey: ['secondaryvideokey', 'secondaryvideo', 'alternatevideo', 'altvideo'],
  ThumbnailKey: ['thumbnailkey', 'thumbnail'],
  ExerciseTrackerInstructionKey: ['exercisetrackerinstructionkey', 'trackerinstructionkey', 'instructionkey', 'trackerinstructions'],
  ShortDescription: ['shortdescription', 'description'],
  SafetyNotes: ['safetynotes', 'safety', 'notes'],
  CaloriesPerMinute: ['caloriesperminute', 'calories', 'cal'],
  IsActive: ['isactive', 'active']
}

const EXERCISE_CSV_FALLBACK_INDEX: Record<CsvColumnKey, number> = {
  Code: 0,
  Name: 1,
  Category: 2,
  IsTrueElevation: 3,
  IsArtificialElevation: 4,
  ContributesToDistance: 5,
  PackUsage: 6,
  IsKneeFriendly: 7,
  ExerciseLocation: 8,
  Sidedness: 9,
  VideoKey: 10,
  SecondaryVideoKey: 11,
  ThumbnailKey: 12,
  ExerciseTrackerInstructionKey: 13,
  ShortDescription: 14,
  SafetyNotes: 15,
  CaloriesPerMinute: 16,
  IsActive: 17
}

export const ExercisesAdminPage = () => {
  const { user } = useAuth()
  const { addToast } = useToast()
  const [exercises, setExercises] = useState<Exercise[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deletingAll, setDeletingAll] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [form, setForm] = useState<ExercisePayload>(initialForm)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [categoryFilter, setCategoryFilter] = useState<ExerciseCategory | 'all'>('all')
  const [viewMode, setViewMode] = useState<ViewMode>('cards')
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'name', direction: 'asc' })
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isExerciseCsvImporting, setIsExerciseCsvImporting] = useState(false)
  const [isExerciseCsvExporting, setIsExerciseCsvExporting] = useState(false)
  const exerciseCsvInputRef = useRef<HTMLInputElement | null>(null)

  const role = (user?.role ?? 'Guest').toString()
  const isEditor = role === 'Admin' || role === 'Editor'

  const loadExercises = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getExercises()
      setExercises(data)
    } catch (error) {
      console.error('Failed to load exercises', error)
      addToast({
        title: 'Could not load exercises',
        description: 'Please refresh or try again later.'
      })
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    if (!isEditor) {
      setLoading(false)
      return
    }
    void loadExercises()
  }, [isEditor, loadExercises])

  const filteredExercises = useMemo(() => {
    let list = [...exercises]
    if (categoryFilter !== 'all') {
      list = list.filter((ex) => ex.category === categoryFilter)
    }
    return list.sort((a, b) => {
      const { key, direction } = sortConfig
      const compareName = (left: Exercise, right: Exercise) =>
        compareExercises(left.name, right.name, direction)

      switch (key) {
        case 'category':
          return compareExercises(a.category, b.category, direction) || compareName(a, b)
        case 'code':
          return compareExercises(a.code, b.code, direction)
        case 'packUsage':
          return (direction === 'asc'
            ? packUsageRank[a.packUsage] - packUsageRank[b.packUsage]
            : packUsageRank[b.packUsage] - packUsageRank[a.packUsage]) || compareName(a, b)
        case 'exerciseLocation':
          return (
            (direction === 'asc'
              ? exerciseLocationRank[a.exerciseLocation] - exerciseLocationRank[b.exerciseLocation]
              : exerciseLocationRank[b.exerciseLocation] - exerciseLocationRank[a.exerciseLocation]) ||
            compareName(a, b)
          )
        case 'isActive':
          return compareBooleans(a.isActive, b.isActive, direction) || compareName(a, b)
        case 'createdAt':
          return compareDates(direction, a.createdAt, b.createdAt) || compareName(a, b)
        default:
          return compareName(a, b)
      }
    })
  }, [exercises, categoryFilter, sortConfig])

  const requestSort = (key: ExerciseSortKey) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { key, direction: 'asc' }
    })
  }

  const resetForm = () => {
    setForm(initialForm)
    setEditingId(null)
    setFormError(null)
    setIsEditModalOpen(false)
  }

  const openAddExerciseDialog = () => {
    resetForm()
    setIsEditModalOpen(true)
  }

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      const trimmedCode = form.code.trim().toUpperCase().replace(/\s+/g, '_')
      const trimmedName = form.name.trim()

      if (!trimmedCode || !trimmedName) {
        setFormError('Both code and name are required.')
        return
      }

      setFormError(null)
      setSaving(true)

      try {
        const payload: ExercisePayload = {
          ...form,
          code: trimmedCode,
          name: trimmedName,
          shortDescription: form.shortDescription?.trim() || null,
          safetyNotes: form.safetyNotes?.trim() || null,
          videoKey: form.videoKey?.trim() || null,
          secondaryVideoKey: form.secondaryVideoKey?.trim() || null,
          thumbnailKey: form.thumbnailKey?.trim() || null,
          exerciseTrackerInstructionKey: form.exerciseTrackerInstructionKey?.trim().toLowerCase() || null
        }

        if (editingId) {
          const updated = await updateExercise(editingId, payload)
          setExercises((prev) => prev.map((item) => (item.id === editingId ? updated : item)))
          addToast({ title: 'Exercise updated', description: 'Changes saved successfully.' })
        } else {
          const created = await createExercise(payload)
          setExercises((prev) => [created, ...prev])
          addToast({ title: 'Exercise added', description: 'New exercise created successfully.' })
        }
        resetForm()
      } catch (error) {
        console.error('Failed to save exercise', error)
        const message =
          error instanceof Error && error.message.includes('409')
            ? 'An exercise with this code already exists.'
            : 'Could not save this exercise. Please try again.'
        addToast({
          title: 'Save failed',
          description: message
        })
      } finally {
        setSaving(false)
      }
    },
    [editingId, form, addToast]
  )

  const triggerExerciseCsvImport = () => {
    if (exerciseCsvInputRef.current) {
      exerciseCsvInputRef.current.value = ''
      exerciseCsvInputRef.current.click()
    }
  }

  const handleExportExercisesCsv = async () => {
    setIsExerciseCsvExporting(true)
    try {
      const header = [...EXERCISE_CSV_COLUMNS]
      const rows = exercises.map((exercise) => [
        exercise.code,
        exercise.name,
        exercise.category,
        exercise.isTrueElevation.toString(),
        exercise.isArtificialElevation.toString(),
        exercise.contributesToDistance.toString(),
        exercise.packUsage,
        exercise.isKneeFriendly.toString(),
        exercise.exerciseLocation,
        exercise.sidedness,
        exercise.videoKey ?? '',
        exercise.secondaryVideoKey ?? '',
        exercise.thumbnailKey ?? '',
        exercise.exerciseTrackerInstructionKey ?? '',
        exercise.shortDescription ?? '',
        exercise.safetyNotes ?? '',
        exercise.caloriesPerMinute != null ? exercise.caloriesPerMinute.toString() : '',
        exercise.isActive.toString()
      ])

      const timestamp = new Date().toISOString().slice(0, 10)
      downloadCsv(header, rows, `hikestrong-exercises-${timestamp}.csv`)
      addToast({ title: 'CSV exported', description: `Exported ${rows.length} exercises.` })
    } catch (error) {
      console.error('Failed to export exercises CSV', error)
      addToast({ title: 'Export failed', description: 'Could not export the exercise CSV file.' })
    } finally {
      setIsExerciseCsvExporting(false)
    }
  }

  const parseCategory = (value: string): ExerciseCategory | null => {
    const normalized = normalizeText(value)
    return EXERCISE_CATEGORIES.find((category) => normalizeText(category) === normalized) ?? null
  }

  const parsePackUsage = (value: string): PackUsage => {
    const normalized = normalizeText(value)
    if (normalized === 'required' || normalized === 'packrequired') return 'Required'
    if (normalized === 'optional' || normalized === 'packoptional') return 'Optional'
    return 'NoPack'
  }

  const parseExerciseLocation = (value: string): ExerciseLocation => {
    const normalized = normalizeText(value)
    if (normalized === 'inside') return 'Inside'
    if (normalized === 'outside') return 'Outside'
    return 'Either'
  }

  const parseExerciseSidedness = (value: string): ExerciseSidedness => {
    const normalized = normalizeText(value)
    if (
      normalized === 'nonsided' ||
      normalized === 'nonside' ||
      normalized === 'single' ||
      normalized === 'oneside' ||
      normalized === 'once'
    ) {
      return 'NonSided'
    }
    return 'Sided'
  }

  const parseExercisesCsvRows = async (file: File) => {
    const text = await file.text()
    // Parse the whole document so quoted multi-line descriptions from Export CSV round-trip (BH-18).
    const rows = parseCsvText(text)
    if (rows.length === 0) {
      throw new Error('CSV file is empty.')
    }

    const header = rows[0].map((value) => value.trim())
    const hasHeader =
      header.some((column) => normalizeText(column) === 'code') &&
      header.some((column) => normalizeText(column) === 'name')

    const dataRows = hasHeader ? rows.slice(1) : rows
    if (dataRows.length === 0) {
      throw new Error('CSV appears to contain only headers.')
    }

    const headerIndex = hasHeader ? buildHeaderIndex(header, EXERCISE_HEADER_ALIASES) : null
    const existingByCode = new Map(
      exercises.map((exercise) => [normalizeExerciseCode(exercise.code), exercise])
    )
    const rowsToImport: ParsedCsvExerciseImportRow[] = []

    const getValue = makeGetValue(headerIndex, EXERCISE_CSV_FALLBACK_INDEX)

    dataRows.forEach((row, index) => {
      const lineNumber = hasHeader ? index + 2 : index + 1
      const code = normalizeExerciseCode(getValue(row, 'Code'))
      const name = getValue(row, 'Name').trim()
      const category = parseCategory(getValue(row, 'Category'))

      if (!code || !name) {
        throw new Error(`Row ${lineNumber}: missing required code or name.`)
      }

      if (!category) {
        throw new Error(`Row ${lineNumber}: invalid category "${getValue(row, 'Category')}".`)
      }

      const calories = parseNumberOrNull(getValue(row, 'CaloriesPerMinute'))
      if (calories !== null && calories < 0) {
        throw new Error(`Row ${lineNumber}: calories must be zero or greater.`)
      }

      const existingMatch = existingByCode.get(code)

      rowsToImport.push({
        action: existingMatch ? 'update' : 'create',
        targetId: existingMatch?.id,
        payload: {
          code,
          name,
          category,
          isTrueElevation: parseBooleanValue(getValue(row, 'IsTrueElevation')),
          isArtificialElevation: parseBooleanValue(getValue(row, 'IsArtificialElevation')),
          contributesToDistance: parseBooleanValue(getValue(row, 'ContributesToDistance')),
          packUsage: parsePackUsage(getValue(row, 'PackUsage')),
          isKneeFriendly: parseBooleanValue(getValue(row, 'IsKneeFriendly')),
          exerciseLocation: parseExerciseLocation(getValue(row, 'ExerciseLocation')),
          sidedness:
            hasHeader && headerIndex?.Sidedness === undefined
              ? 'Sided'
              : parseExerciseSidedness(getValue(row, 'Sidedness')),
          videoKey: getValue(row, 'VideoKey') || null,
          secondaryVideoKey: getValue(row, 'SecondaryVideoKey') || null,
          thumbnailKey: getValue(row, 'ThumbnailKey') || null,
          exerciseTrackerInstructionKey: getValue(row, 'ExerciseTrackerInstructionKey') || null,
          shortDescription: getValue(row, 'ShortDescription') || null,
          safetyNotes: getValue(row, 'SafetyNotes') || null,
          caloriesPerMinute: calories,
          isActive: parseBooleanValue(getValue(row, 'IsActive') || 'true')
        }
      })
    })

    return rowsToImport
  }

  const handleImportExercisesCsv = async (file: File) => {
    setIsExerciseCsvImporting(true)
    try {
      const rowsToImport = await parseExercisesCsvRows(file)
      let created = 0
      let updated = 0
      const errors: string[] = []

      for (const row of rowsToImport) {
        try {
          if (row.action === 'update' && row.targetId) {
            await updateExercise(row.targetId, row.payload)
            updated += 1
          } else {
            await createExercise(row.payload)
            created += 1
          }
        } catch (error) {
          console.error('Failed to import exercise', row.payload.code, error)
          errors.push(row.payload.code)
        }
      }

      if (created > 0 || updated > 0) {
        await loadExercises()
      }

      const imported = created + updated
      if (errors.length > 0) {
        addToast({
          title: 'Import completed with errors',
          description: `Imported ${imported} exercises with ${errors.length} failed.`
        })
        return
      }

      const details = []
      if (created > 0) {
        details.push(`${created} created`)
      }
      if (updated > 0) {
        details.push(`${updated} updated`)
      }

      const description = details.length > 0 ? `Imported ${details.join(' and ')}.` : 'No exercises imported.'

      addToast({
        title: 'Import complete',
        description
      })
    } catch (error) {
      console.error('Failed to import exercises CSV', error)
      addToast({
        title: 'Import failed',
        description: error instanceof Error ? error.message : 'Could not parse or import the CSV file.'
      })
    } finally {
      setIsExerciseCsvImporting(false)
    }
  }

  const handleEdit = (exercise: Exercise) => {
    setEditingId(exercise.id)
    setForm({
      code: exercise.code,
      name: exercise.name,
      category: exercise.category,
      isTrueElevation: exercise.isTrueElevation,
      isArtificialElevation: exercise.isArtificialElevation,
      contributesToDistance: exercise.contributesToDistance,
      packUsage: exercise.packUsage,
      isKneeFriendly: exercise.isKneeFriendly,
      exerciseLocation: exercise.exerciseLocation,
      sidedness: exercise.sidedness,
      videoKey: exercise.videoKey ?? null,
      secondaryVideoKey: exercise.secondaryVideoKey ?? null,
      thumbnailKey: exercise.thumbnailKey ?? null,
      exerciseTrackerInstructionKey: exercise.exerciseTrackerInstructionKey ?? null,
      shortDescription: exercise.shortDescription ?? null,
      safetyNotes: exercise.safetyNotes ?? null,
      caloriesPerMinute: exercise.caloriesPerMinute ?? null,
      isActive: exercise.isActive
    })
    setFormError(null)
    setIsEditModalOpen(true)
  }

  const handleDelete = async (id: string) => {
    const target = exercises.find((item) => item.id === id)
    if (!target) {
      return
    }

    const confirmed = window.confirm(`Delete the exercise "${target.name}"?`)
    if (!confirmed) {
      return
    }

    setDeletingId(id)
    try {
      await deleteExercise(id)
      setExercises((prev) => prev.filter((item) => item.id !== id))
      addToast({ title: 'Exercise removed', description: 'It has been deleted from the library.' })
      if (editingId === id) {
        resetForm()
      }
    } catch (error) {
      console.error('Failed to delete exercise', error)
      addToast({ title: 'Delete failed', description: 'Unable to delete this exercise right now.' })
    } finally {
      setDeletingId(null)
    }
  }

  // Inline update for quick flag/pack toggles
  const handleInlineUpdate = async (exercise: Exercise, updates: Partial<ExercisePayload>) => {
    try {
      const payload: ExercisePayload = {
        code: exercise.code,
        name: exercise.name,
        category: exercise.category,
        isTrueElevation: exercise.isTrueElevation,
        isArtificialElevation: exercise.isArtificialElevation,
        contributesToDistance: exercise.contributesToDistance,
        packUsage: exercise.packUsage,
        isKneeFriendly: exercise.isKneeFriendly,
        exerciseLocation: exercise.exerciseLocation,
        sidedness: exercise.sidedness,
        videoKey: exercise.videoKey ?? null,
        secondaryVideoKey: exercise.secondaryVideoKey ?? null,
        thumbnailKey: exercise.thumbnailKey ?? null,
        exerciseTrackerInstructionKey: exercise.exerciseTrackerInstructionKey ?? null,
        shortDescription: exercise.shortDescription ?? null,
        safetyNotes: exercise.safetyNotes ?? null,
        caloriesPerMinute: exercise.caloriesPerMinute ?? null,
        isActive: exercise.isActive,
        ...updates
      }
      const updated = await updateExercise(exercise.id, payload)
      setExercises((prev) => prev.map((item) => (item.id === exercise.id ? updated : item)))
    } catch (error) {
      console.error('Failed to update exercise', error)
      addToast({ title: 'Update failed', description: 'Could not save changes.' })
    }
  }

  const generateCopyCode = (code: string) => {
    const existingCodes = new Set(exercises.map((item) => item.code.toUpperCase()))
    const baseCode = `${code.toUpperCase()}_COPY`
    if (!existingCodes.has(baseCode)) {
      return baseCode
    }

    let index = 1
    let candidate = `${baseCode}_${index}`
    while (existingCodes.has(candidate)) {
      index += 1
      candidate = `${baseCode}_${index}`
    }
    return candidate
  }

  const handleDuplicate = async (exercise: Exercise) => {
    const confirmed = window.confirm(`Duplicate "${exercise.name}"?`)
    if (!confirmed) {
      return
    }

    const duplicatePayload: ExercisePayload = {
      code: generateCopyCode(exercise.code),
      name: `${exercise.name} (Copy)`,
      category: exercise.category,
      isTrueElevation: exercise.isTrueElevation,
      isArtificialElevation: exercise.isArtificialElevation,
      contributesToDistance: exercise.contributesToDistance,
      packUsage: exercise.packUsage,
      isKneeFriendly: exercise.isKneeFriendly,
      exerciseLocation: exercise.exerciseLocation,
      sidedness: exercise.sidedness,
      videoKey: exercise.videoKey ?? null,
      secondaryVideoKey: exercise.secondaryVideoKey ?? null,
      thumbnailKey: exercise.thumbnailKey ?? null,
      exerciseTrackerInstructionKey: exercise.exerciseTrackerInstructionKey ?? null,
      shortDescription: exercise.shortDescription ?? null,
      safetyNotes: exercise.safetyNotes ?? null,
      caloriesPerMinute: exercise.caloriesPerMinute ?? null,
      isActive: exercise.isActive
    }
    try {
      const duplicated = await createExercise(duplicatePayload)
      setExercises((prev) => [duplicated, ...prev])
      addToast({ title: 'Exercise duplicated', description: `"${duplicated.name}" added to library.` })
    } catch (error) {
      console.error('Failed to duplicate exercise', error)
      addToast({
        title: 'Duplicate failed',
        description: 'Could not duplicate this exercise. Please try again.'
      })
    }
  }

  const handleDeleteAll = async () => {
    const confirmed = window.confirm(
      'WARNING: This will permanently delete ALL exercises from the database. This action cannot be undone. Are you absolutely sure?'
    )
    if (!confirmed) {
      return
    }

    const doubleConfirmed = window.confirm(
      'Final confirmation: Delete all exercises? Click OK to proceed.'
    )
    if (!doubleConfirmed) {
      return
    }

    setDeletingAll(true)
    try {
      const result = await deleteAllExercises()
      setExercises([])
      resetForm()
      addToast({
        title: 'All exercises deleted',
        description: `Removed ${result.deletedCount} exercises from the database.`
      })
    } catch (error) {
      console.error('Failed to delete all exercises', error)
      addToast({ title: 'Delete failed', description: 'Unable to delete all exercises.' })
    } finally {
      setDeletingAll(false)
    }
  }

  const renderSortIndicator = (key: ExerciseSortKey) => {
    if (sortConfig.key !== key) {
      return (
        <span className="text-muted-foreground/50" aria-hidden>
          ⇅
        </span>
      )
    }
    return sortConfig.direction === 'asc' ? (
      <span aria-hidden>↑</span>
    ) : (
      <span aria-hidden>↓</span>
    )
  }

  if (!isEditor) {
    return (
      <Card className="border-dashed border-amber-500/40 bg-amber-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="h-5 w-5" />
            Limited Access
          </CardTitle>
          <CardDescription>
            Exercises can only be managed by Admin or Editor accounts. Contact an admin if you need
            access.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const isEditing = editingId !== null
  const exerciseEditorCard = (
    <Card className="w-full shadow-md">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Dumbbell className="h-5 w-5 text-primary" />
              {isEditing ? 'Edit exercise' : 'Add an exercise'}
            </CardTitle>
            <CardDescription>Code should be unique (e.g., STEP_UPS, HILL_REPEATS).</CardDescription>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={resetForm}
            disabled={saving}
            title={isEditing ? 'Close edit modal' : 'Close add exercise modal'}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="exercise-code">
                Code
              </label>
              <Input
                id="exercise-code"
                value={form.code}
                onChange={(event) => setForm((prev) => ({ ...prev, code: event.target.value }))}
                placeholder="STEP_UPS"
                disabled={saving}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="exercise-name">
                Name
              </label>
              <Input
                id="exercise-name"
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Step-Ups"
                disabled={saving}
              />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="exercise-category">
              Category
            </label>
            <select
              id="exercise-category"
              value={form.category}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, category: event.target.value as ExerciseCategory }))
              }
              disabled={saving}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {EXERCISE_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="exercise-description">
              Short Description
            </label>
            <Textarea
              id="exercise-description"
              value={form.shortDescription ?? ''}
              onChange={(event) => setForm((prev) => ({ ...prev, shortDescription: event.target.value }))}
              rows={2}
              placeholder="Brief description of the exercise."
              disabled={saving}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="exercise-safety">
              Safety Notes
            </label>
            <Textarea
              id="exercise-safety"
              value={form.safetyNotes ?? ''}
              onChange={(event) => setForm((prev) => ({ ...prev, safetyNotes: event.target.value }))}
              rows={2}
              placeholder="Contraindications or safety warnings."
              disabled={saving}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="exercise-video">
                Primary Video Key
              </label>
              <Input
                id="exercise-video"
                value={form.videoKey ?? ''}
                onChange={(event) => setForm((prev) => ({ ...prev, videoKey: event.target.value }))}
                placeholder="weighted_lunge_pack"
                disabled={saving}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="exercise-secondary-video">
                Secondary Video Key
              </label>
              <Input
                id="exercise-secondary-video"
                value={form.secondaryVideoKey ?? ''}
                onChange={(event) => setForm((prev) => ({ ...prev, secondaryVideoKey: event.target.value }))}
                placeholder="weighted_lunge_dumbbells"
                disabled={saving}
              />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="exercise-thumbnail">
              Thumbnail Key
            </label>
            <Input
              id="exercise-thumbnail"
              value={form.thumbnailKey ?? ''}
              onChange={(event) => setForm((prev) => ({ ...prev, thumbnailKey: event.target.value }))}
              placeholder="media/thumbs/..."
              disabled={saving}
            />
            <p className="text-xs text-muted-foreground">
              Secondary thumbnails come from the alternate exercise video record automatically.
            </p>
          </div>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Primary video is shown first in the app. If you add a secondary video, the workout demo screen
              presents it as the alternate picture-in-picture option.
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="exercise-tracker-instruction">
              Exercise Tracker Instruction Key
            </label>
            <Input
              id="exercise-tracker-instruction"
              value={form.exerciseTrackerInstructionKey ?? ''}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, exerciseTrackerInstructionKey: event.target.value }))
              }
              placeholder="stairs_tracker"
              disabled={saving}
            />
            <p className="text-xs text-muted-foreground">
              Link this exercise to an entry from the Exercise Tracker Instructions library.
            </p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="exercise-calories">
              Calories per minute (optional)
            </label>
            <Input
              id="exercise-calories"
              type="number"
              step="0.1"
              min="0"
              value={form.caloriesPerMinute ?? ''}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  caloriesPerMinute: event.target.value ? parseFloat(event.target.value) : null
                }))
              }
              placeholder="e.g., 8.5"
              disabled={saving}
            />
          </div>
          <div className="space-y-3">
            <p className="text-sm font-medium text-foreground">Training Flags</p>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isTrueElevation}
                  onChange={(event) => setForm((prev) => ({ ...prev, isTrueElevation: event.target.checked }))}
                  disabled={saving}
                  className="rounded border-gray-300"
                />
                True Elevation
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isArtificialElevation}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, isArtificialElevation: event.target.checked }))
                  }
                  disabled={saving}
                  className="rounded border-gray-300"
                />
                Artificial Elevation
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.contributesToDistance}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, contributesToDistance: event.target.checked }))
                  }
                  disabled={saving}
                  className="rounded border-gray-300"
                />
                Contributes to Distance
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isKneeFriendly}
                  onChange={(event) => setForm((prev) => ({ ...prev, isKneeFriendly: event.target.checked }))}
                  disabled={saving}
                  className="rounded border-gray-300"
                />
                Knee Friendly
              </label>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="exercise-pack-usage">
                Pack Usage
              </label>
              <select
                id="exercise-pack-usage"
                value={form.packUsage}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, packUsage: event.target.value as PackUsage }))
                }
                disabled={saving}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {PACK_USAGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="exercise-location">
                Exercise Location
              </label>
              <select
                id="exercise-location"
                value={form.exerciseLocation}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, exerciseLocation: event.target.value as ExerciseLocation }))
                }
                disabled={saving}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {EXERCISE_LOCATIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="exercise-sidedness">
                Side Handling
              </label>
              <select
                id="exercise-sidedness"
                value={form.sidedness}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, sidedness: event.target.value as ExerciseSidedness }))
                }
                disabled={saving}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {EXERCISE_SIDEDNESS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
                disabled={saving}
                className="rounded border-gray-300"
              />
              Active (available for use)
            </label>
          </div>

          {formError ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {formError}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={saving} className="gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {editingId ? 'Save changes' : 'Add exercise'}
            </Button>
            {editingId ? (
              <Button type="button" variant="outline" onClick={resetForm} disabled={saving}>
                Cancel edit
              </Button>
            ) : null}
          </div>
        </form>
      </CardContent>
    </Card>
  )

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        <Card className="w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-100 shadow-xl">
          <CardHeader className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-emerald-500/20 p-3 ring-1 ring-emerald-500/30">
                <Dumbbell className="h-6 w-6 text-emerald-200" />
              </div>
              <div>
                <CardTitle className="text-lg">Exercise Library</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Manage exercises used in training plan generation.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            <div className="flex flex-wrap gap-2">
              <input
                ref={exerciseCsvInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  if (file) {
                    void handleImportExercisesCsv(file)
                  }
                }}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={triggerExerciseCsvImport}
                disabled={isExerciseCsvImporting || loading || deletingAll}
                className="gap-2"
              >
                {isExerciseCsvImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Import CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleExportExercisesCsv()}
                disabled={isExerciseCsvExporting || loading}
                className="gap-2"
              >
                {isExerciseCsvExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Export CSV
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={openAddExerciseDialog}
                className="gap-2"
              >
                <Sparkles className="h-4 w-4" />
                Add exercise
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteAll}
                disabled={deletingAll || loading || exercises.length === 0}
                className="gap-2"
              >
                {deletingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Delete All
              </Button>
            </div>
          </CardContent>
        </Card>

        {isEditModalOpen ? (
          <Dialog open={isEditModalOpen} onOpenChange={(isOpen) => !isOpen && resetForm()}>
            <DialogContent>
              <DialogTitle className="sr-only">{isEditing ? 'Edit exercise' : 'Add an exercise'}</DialogTitle>
              <DialogDescription className="sr-only">Use this form to update exercise details.</DialogDescription>
              {exerciseEditorCard}
            </DialogContent>
          </Dialog>
        ) : null}
      </div>

      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Exercise Library</h2>
            <p className="text-sm text-muted-foreground">
              {filteredExercises.length} exercise
              {filteredExercises.length === 1 ? '' : 's'}
              {categoryFilter !== 'all' ? ` in ${categoryFilter}` : ''}.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-md border border-input">
              <Button
                variant={viewMode === 'cards' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('cards')}
                className="rounded-r-none"
                type="button"
              >
                <LayoutGrid className="h-4 w-4" />
                Cards
              </Button>
              <Button
                variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('table')}
                className="rounded-l-none"
                type="button"
              >
                <Table2 className="h-4 w-4" />
                Table
              </Button>
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as ExerciseCategory | 'all')}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="all">All Categories</option>
              {EXERCISE_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <Card className="flex items-center gap-3 px-6 py-5">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading exercises…</p>
          </Card>
        ) : filteredExercises.length === 0 ? (
          <EmptyState
            icon={Dumbbell}
            title="No exercises yet"
            description="Add the first exercise to populate the library."
          >
            <Button onClick={openAddExerciseDialog} className="gap-2">
              <Pencil className="h-4 w-4" />
              Add exercise
            </Button>
          </EmptyState>
        ) : viewMode === 'cards' ? (
          <div className="grid gap-4 md:grid-cols-2">
            {filteredExercises.map((exercise) => (
              <Card
                key={exercise.id}
                className={`flex flex-col justify-between shadow-sm ${!exercise.isActive ? 'opacity-60' : ''}`}
              >
                <CardHeader className="space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-lg flex items-start gap-2">
                      <Dumbbell className="mt-1 h-4 w-4 text-primary" />
                      <span>{exercise.name}</span>
                    </CardTitle>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${categoryColors[exercise.category]}`}
                    >
                      {exercise.category}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">{exercise.code}</p>
                  <p className="text-xs text-muted-foreground">
                    Location:{' '}
                    <span
                      className={`rounded-full px-2 py-0.5 font-medium ${exerciseLocationColors[exercise.exerciseLocation]}`}
                    >
                      {exercise.exerciseLocation}
                    </span>
                  </p>
                  {exercise.shortDescription ? (
                    <CardDescription className="text-sm leading-relaxed text-foreground">
                      {exercise.shortDescription}
                    </CardDescription>
                  ) : null}
                  {exercise.exerciseTrackerInstructionKey ? (
                    <p className="text-xs text-muted-foreground">
                      Tracker instructions: <span className="font-mono">{exercise.exerciseTrackerInstructionKey}</span>
                    </p>
                  ) : null}
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    {exercise.isTrueElevation ? (
                      <span className="px-2 py-0.5 rounded bg-green-100 text-green-700">True Elev</span>
                    ) : null}
                    {exercise.isArtificialElevation ? (
                      <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700">Artif Elev</span>
                    ) : null}
                    {exercise.contributesToDistance ? (
                      <span className="px-2 py-0.5 rounded bg-cyan-100 text-cyan-700">Distance</span>
                    ) : null}
                    {exercise.isKneeFriendly ? (
                      <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-700">Knee OK</span>
                    ) : null}
                    {exercise.sidedness === 'NonSided' ? (
                      <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">Non-sided</span>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => handleInlineUpdate(exercise, { isActive: !exercise.isActive })}
                      className={`px-2 py-0.5 rounded cursor-pointer transition-colors ${
                        exercise.isActive
                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                          : 'bg-red-100 text-red-600 hover:bg-red-200'
                      }`}
                      title="Toggle Active"
                    >
                      {exercise.isActive ? 'Active' : 'Inactive'}
                    </button>
                    <select
                      value={exercise.packUsage}
                      onChange={(e) => handleInlineUpdate(exercise, { packUsage: e.target.value as PackUsage })}
                      className={`px-2 py-0.5 rounded cursor-pointer text-xs border-0 ${
                        exercise.packUsage === 'Required'
                          ? 'bg-orange-100 text-orange-700'
                          : exercise.packUsage === 'Optional'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                      title="Pack Usage"
                    >
                      <option value="NoPack">No Pack</option>
                      <option value="Optional">Pack Optional</option>
                      <option value="Required">Pack Required</option>
                    </select>
                  </div>
                </CardHeader>
                <CardContent className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <Clock3 className="h-4 w-4" />
                    {formatDate(exercise.createdAt)}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(exercise)}
                      disabled={saving || deletingId === exercise.id}
                      className="gap-1"
                    >
                      <Pencil className="h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => void handleDelete(exercise.id)}
                      disabled={deletingId === exercise.id || saving}
                    >
                      {deletingId === exercise.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-muted/60">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 font-medium">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground"
                      onClick={() => requestSort('name')}
                    >
                      Name {renderSortIndicator('name')}
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground"
                      onClick={() => requestSort('code')}
                    >
                      Code {renderSortIndicator('code')}
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground"
                      onClick={() => requestSort('category')}
                    >
                      Category {renderSortIndicator('category')}
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">Flags</th>
                  <th className="px-3 py-2 font-medium">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground"
                      onClick={() => requestSort('packUsage')}
                    >
                      Pack {renderSortIndicator('packUsage')}
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground"
                      onClick={() => requestSort('exerciseLocation')}
                    >
                      Location {renderSortIndicator('exerciseLocation')}
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground"
                      onClick={() => requestSort('isActive')}
                    >
                      Active {renderSortIndicator('isActive')}
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-foreground"
                      onClick={() => requestSort('createdAt')}
                    >
                      Created {renderSortIndicator('createdAt')}
                    </button>
                  </th>
                  <th className="px-3 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60 bg-background">
                {filteredExercises.map((exercise) => (
                  <tr
                    key={exercise.id}
                    className={`${!exercise.isActive ? 'opacity-60' : ''}`}
                  >
                    <td className="px-3 py-2 font-medium text-foreground">
                      <div className="inline-flex items-center gap-1.5">
                        <Dumbbell className="h-4 w-4 text-primary" />
                        {exercise.name}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono text-muted-foreground">{exercise.code}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${categoryColors[exercise.category]}`}
                      >
                        {exercise.category}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1.5 text-xs">
                        {exercise.isTrueElevation ? (
                          <span className="rounded-md px-2 py-1 text-emerald-700">True</span>
                        ) : null}
                        {exercise.isArtificialElevation ? (
                          <span className="rounded-md px-2 py-1 text-blue-700">Artificial</span>
                        ) : null}
                        {exercise.contributesToDistance ? (
                          <span className="rounded-md px-2 py-1 text-cyan-700">Dist</span>
                        ) : null}
                        {exercise.isKneeFriendly ? (
                          <span className="rounded-md px-2 py-1 text-purple-700">KneeOK</span>
                        ) : null}
                        {exercise.sidedness === 'NonSided' ? (
                          <span className="rounded-md px-2 py-1 text-slate-700">Non-sided</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={exercise.packUsage}
                        onChange={(e) => handleInlineUpdate(exercise, { packUsage: e.target.value as PackUsage })}
                        className={`rounded-md border border-input px-2 py-1 text-xs ${
                          exercise.packUsage === 'Required'
                            ? 'bg-orange-100 text-orange-700'
                            : exercise.packUsage === 'Optional'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        <option value="NoPack">No Pack</option>
                        <option value="Optional">Pack Optional</option>
                        <option value="Required">Pack Required</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={exercise.exerciseLocation}
                        onChange={(e) =>
                          handleInlineUpdate(exercise, {
                            exerciseLocation: e.target.value as ExerciseLocation
                          })
                        }
                        className={`rounded-md border border-input bg-background px-2 py-1 text-xs ${exerciseLocationColors[exercise.exerciseLocation]}`}
                        title="Edit exercise location"
                      >
                        <option value="Either">Either</option>
                        <option value="Inside">Inside</option>
                        <option value="Outside">Outside</option>
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => handleInlineUpdate(exercise, { isActive: !exercise.isActive })}
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          exercise.isActive
                            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                            : 'bg-red-100 text-red-600 hover:bg-red-200'
                        }`}
                      >
                        {exercise.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{formatDate(exercise.createdAt)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(exercise)}
                          disabled={saving || deletingId === exercise.id}
                          title="Edit"
                          className="h-7 w-7 p-0 gap-0"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Duplicate"
                          onClick={() => void handleDuplicate(exercise)}
                          disabled={saving}
                          className="h-7 w-7 p-0 gap-0"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 gap-0 text-red-600 hover:bg-red-50"
                            onClick={() => void handleDelete(exercise.id)}
                            disabled={deletingId === exercise.id || saving}
                            title="Delete"
                          >
                          {deletingId === exercise.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
